import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useTerminal } from "@/lib/terminal";
import { PriceChart } from "@/components/PriceChart";
import { TradePanel } from "@/components/TradePanel";
import { usd, price, age, short, EXPLORER } from "@/lib/format";

const Stat = ({ label, value, id }) => (
  <div className="px-3 py-2 border border-[#222634] rounded bg-[#101216]">
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono text-sm text-slate-100" data-testid={id}>{value}</p>
  </div>
);

const Trades = ({ trades }) => (
  <div className="border border-[#222634] rounded-md bg-[#101216] overflow-hidden" data-testid="recent-trades">
    <h3 className="px-3 h-9 flex items-center text-sm font-semibold border-b border-[#222634]">Son İşlemler</h3>
    <div className="max-h-[320px] overflow-y-auto terminal-scroll">
      <table className="w-full text-xs font-mono">
        <tbody>
          {trades.map((t) => (
            <tr key={`${t.txHash}-${t.logIndex}`} className="border-b border-[#222634]/50 hover:bg-[#161920]">
              <td className={`px-3 py-1.5 font-semibold ${t.kind === "buy" ? "text-[#00E676]" : "text-[#FF5252]"}`}>{t.kind === "buy" ? "AL" : "SAT"}</td>
              <td className="px-2 text-slate-200">{usd(t.volumeUsd)}</td>
              <td className="px-2 text-slate-400">{Number(t.volumeQuote).toFixed(4)}</td>
              <td className="px-2 text-slate-500"><a href={`${EXPLORER}/address/${t.user}`} target="_blank" rel="noreferrer" className="hover:text-slate-200">{short(t.user)}</a></td>
              <td className="px-3 text-right text-slate-500">{age(t.timestamp)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {trades.length === 0 && <p className="p-4 text-slate-500 text-sm">İşlem yok</p>}
    </div>
  </div>
);

export default function TokenPage() {
  const { address } = useParams();
  const { api } = useTerminal();
  const [p, setP] = useState(null);
  const [trades, setTrades] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([api("get", `/pools/${address}`), api("get", `/pools/${address}/trades`)]);
      setP(d); setTrades(t.docs || []);
    } catch {}
  }, [api, address]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const tk = p?.token;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/" data-testid="back-to-pulse" className="p-1.5 rounded border border-[#222634] hover:bg-[#161920] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
        {tk?.imageUrl && <img src={tk.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />}
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="token-symbol">{tk?.symbol || "…"} <span className="text-base font-normal text-slate-400">{tk?.name}</span></h1>
          <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
            {short(address)}
            <button data-testid="copy-token-address" onClick={() => { navigator.clipboard.writeText(address); toast.success("Adres kopyalandı"); }}><Copy className="w-3 h-3 hover:text-slate-200" /></button>
            <a href={`https://www.full.fun/token/${address}`} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 hover:text-slate-200">full.fun <ExternalLink className="w-3 h-3" /></a>
            <a href={`${EXPLORER}/token/${address}`} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 hover:text-slate-200">explorer <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat label="Fiyat" value={price(p?.priceUsd)} id="stat-price" />
        <Stat label="Market Cap" value={usd(p?.marketCapUsd)} id="stat-mcap" />
        <Stat label="24s Hacim" value={usd(p?.volume24hUsd)} id="stat-volume" />
        <Stat label="Likidite" value={usd(p?.liqUsd)} id="stat-liq" />
        <Stat label="Holder" value={p?.holderCount ?? "-"} id="stat-holders" />
        <Stat label="Bonding" value={p ? (p.state === 0 ? `%${((p.progressBps || 0) / 100).toFixed(1)}` : "Graduated") : "-"} id="stat-progress" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-8 space-y-3">
          <PriceChart address={address} />
          <Trades trades={trades} />
        </div>
        <div className="lg:col-span-4 space-y-3">
          <TradePanel token={address} symbol={tk?.symbol || ""} onDone={load} />
          {tk?.description && <p className="text-sm text-slate-400 p-3 border border-[#222634] rounded bg-[#101216]">{tk.description}</p>}
          {tk?.tax && <p className="text-xs font-mono text-[#FFD700] p-3 border border-[#FFD700]/20 rounded bg-[#FFD700]/5" data-testid="token-tax-info">Vergi: alım %{tk.tax.buyBps / 100} · satım %{tk.tax.sellBps / 100} · {age(p?.createdTime)} önce basıldı</p>}
        </div>
      </div>
    </div>
  );
}
